import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading3 } from '~/theme';
import {
    AccordionButton,
    AccordionContainer,
    AccordionContentContainer,
    AccordionItem,
    AccordionTitle,
    ButtonContainer,
    ExpandButton,
    StyledChevronDown,
    StyledChevronUp,
} from './styles';

export interface AccordionPanel {
    name: string;
    title: string;
    content: ReactNode;
    isOpen?: boolean;
}

interface AccordionProps {
    showExpandAll: boolean;
    panelData: AccordionPanel[];
}

interface AccordionsState {
    [key: string]: { isOpen: boolean };
}

const Accordion = ({ showExpandAll, panelData }: AccordionProps) => {
    const { t } = useTranslation('base-layout');
    const [accordionsState, setAccordionsState] = useState<AccordionsState>({});
    const [collectivelyOpen, setCollectivelyOpen] = useState<boolean>(false);

    const trackCollectiveState = (currentAccordionState: AccordionsState) => {
        const allOpenStates = Object.values(currentAccordionState).map(item => item.isOpen);
        const areAllAccordionsOpen = allOpenStates.every(element => element);
        setCollectivelyOpen(areAllAccordionsOpen);
    };

    const handleAccordion = (accordionName: string) => {
        const accordionSlice = accordionsState[accordionName];
        const newAccordionsState = {
            ...accordionsState,
            [accordionName]: {
                ...accordionsState[accordionName],
                isOpen: !accordionSlice.isOpen,
            },
        };
        trackCollectiveState(newAccordionsState);
        setAccordionsState(newAccordionsState);
    };

    const toggleExpandAll = () => {
        const newAccordionsState: AccordionsState = { ...accordionsState };
        if (collectivelyOpen) {
            Object.keys(newAccordionsState).forEach(key => {
                newAccordionsState[key] = { ...newAccordionsState[key], isOpen: false };
            });
        } else {
            Object.keys(newAccordionsState).forEach(key => {
                newAccordionsState[key] = { ...newAccordionsState[key], isOpen: true };
            });
        }
        trackCollectiveState(newAccordionsState);
        setAccordionsState(newAccordionsState);
    };

    useEffect(() => {
        const formattedPanelData = panelData.reduce(
            (acc, item) => Object.assign(acc, { [item.name]: { ...item, isOpen: !!item.isOpen } }),
            {}
        );
        trackCollectiveState(formattedPanelData);
        setAccordionsState(formattedPanelData);
    }, [panelData]);

    return (
        <>
            <ButtonContainer>
                {showExpandAll && (
                    <ExpandButton onClick={toggleExpandAll} type="button">
                        {collectivelyOpen ? t('accordion.collapseAll') : t('accordion.expandAll')}
                    </ExpandButton>
                )}
            </ButtonContainer>
            <AccordionContainer>
                {panelData?.map((data: AccordionPanel) => (
                    <AccordionItem className="accordion" key={data.name}>
                        <AccordionTitle>
                            <Heading3>
                                <AccordionButton
                                    type="button"
                                    onClick={() => handleAccordion(data.name)}
                                    aria-expanded={!!accordionsState[data.name]?.isOpen}
                                    aria-controls={`accordion-content-${data.name}`}
                                >
                                    {accordionsState[data.name]?.isOpen ? (
                                        <StyledChevronUp
                                            className="ontario-icon"
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    ) : (
                                        <StyledChevronDown
                                            className="ontario-icon"
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    )}
                                    {data.title}
                                </AccordionButton>
                            </Heading3>
                        </AccordionTitle>

                        {accordionsState[data.name]?.isOpen && (
                            <AccordionContentContainer id={`accordion-content-${data.name}`}>
                                {data.content}
                            </AccordionContentContainer>
                        )}
                    </AccordionItem>
                ))}
            </AccordionContainer>
        </>
    );
};

export default Accordion;
